import React, { useMemo, useState } from "react";
import { Share2, ListChecks, Loader2, Copy, ExternalLink, Presentation } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { parseQcm } from "@/lib/qcmParse";
import { shareOrigin } from "@/lib/shareText";
import { useL } from "@/lib/useL";
import { ShareDialog } from "@/components/ShareDialog";
import { InfoTip } from "@/components/InfoTip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const btn = "inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.12em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] dark:text-blue-300 hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50";

const QuizLinkDialog = ({ url, onClose }) => {
  const L = useL();
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success(L("Lien copié.", "Link copied.")), () => toast.error(L("Copie impossible.", "Copy failed.")));
  return (
    <Dialog open={!!url} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="quiz-link-dialog">
        <DialogHeader><DialogTitle>{L("Quiz pour les élèves", "Quiz for pupils")}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <input readOnly value={url || ""} className="flex-1 border border-border px-2 py-2 text-xs bg-background" data-testid="quiz-link-url" />
          <button type="button" onClick={copy} className="px-3 border border-[#002FA7] text-[#002FA7]" aria-label={L("Copier", "Copy")} data-testid="quiz-link-copy"><Copy className="w-4 h-4" /></button>
        </div>
        {url && <img src={`${API}/api/qr?data=${encodeURIComponent(url)}`} alt="QR code" className="w-56 h-56 mx-auto" data-testid="quiz-link-qr" />}
        <p className="text-xs text-muted-foreground text-center">{L("Projetez ce QR code ou envoyez le lien : les élèves répondent sur leur téléphone, sans compte, et voient leur note à la fin.", "Project this QR code or send the link: pupils answer on their phone, no account needed, and see their score at the end.")}</p>
        <div className="grid grid-cols-2 gap-2">
          <a href={url} target="_blank" rel="noopener noreferrer" className={`${btn} justify-center`} data-testid="quiz-link-open"><ExternalLink className="w-3.5 h-3.5" /> {L("Essayer le quiz", "Try the quiz")}</a>
          <a href={`${url}?tableau=1`} target="_blank" rel="noopener noreferrer" className={`${btn} justify-center`} data-testid="quiz-link-board"><Presentation className="w-3.5 h-3.5" /> {L("Projeter au tableau", "Show on the board")}</a>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// « Partager le QCM » (questions + corrigé only) and « Quiz pour les élèves » (free, no AI).
export function QcmTools({ text, title, summaryId, testid = "qcm-tools" }) {
  const L = useL();
  const { user } = useAuth();
  const quiz = useMemo(() => parseQcm(text), [text]);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const label = quiz ? L("QCM", "Quiz") : L("Réponse", "Answer");
  const short = (title || "SUX1").length > 70 ? `${(title || "").slice(0, 68).trimEnd()}…` : title || "SUX1";
  const shareTitle = `${label} · ${short}`;

  const makeQuiz = async () => {
    if (!user) return toast(L("Créez un compte gratuit pour partager un quiz.", "Create a free account to share a quiz."));
    setBusy(true);
    try {
      const { data } = await api.post("/quizzes", { title: shareTitle.slice(0, 200), summary_id: summaryId || null, questions: quiz });
      setUrl(`${shareOrigin()}/q/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : L("Impossible de créer le quiz.", "Could not create the quiz."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <button type="button" className={btn} onClick={() => setShareOpen(true)} data-testid={`${testid}-share`}>
        <Share2 className="w-3.5 h-3.5" /> {quiz ? L("Partager le QCM", "Share the quiz") : L("Partager cette réponse", "Share this answer")}
      </button>
      {quiz && (
        <button type="button" className={btn} onClick={makeQuiz} disabled={busy} data-testid={`${testid}-quiz`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-3.5 h-3.5" />} {L(`Quiz pour les élèves (${quiz.length} questions)`, `Quiz for pupils (${quiz.length} questions)`)}
        </button>
      )}
      <InfoTip testid={`${testid}-info`} content={L(
        <><b>Partager</b> : envoie seulement cette partie (questions et corrigé), sans le résumé. {quiz && <><b>Quiz pour les élèves</b> : un lien et un QR code ; les élèves cochent leurs réponses sur leur téléphone puis voient leur note et le corrigé. </>}Gratuit, aucun crédit.</>,
        <><b>Share</b>: sends only this part (questions and answers), without the summary. {quiz && <><b>Quiz for pupils</b>: a link and QR code; pupils tick their answers on their phone, then see their score and the answers. </>}Free, no credit.</>,
      )} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} title={shareTitle} text={text} isPublic={false} publicUrl={null} onEnsurePublic={null} part />
      <QuizLinkDialog url={url} onClose={() => setUrl(null)} />
    </div>
  );
}

export default QcmTools;
